import { ServerErrorCategory } from '@modules/DiagnosticsModule';

type ErrorCategory =
  (typeof ServerErrorCategory)[keyof typeof ServerErrorCategory];

/**
 * Extracts a string message from an unknown error value.
 */
function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (error != null && typeof error === 'object') {
    // Use != null to check for both null and undefined
    if (!(error instanceof Date) && !(error instanceof RegExp)) {
      try {
        return JSON.stringify(error);
      } catch {
        // JSON.stringify failed (circular reference, etc.)
        // Try to extract meaningful info from the object
        const obj = error as Record<string, unknown>;
        if ('message' in obj && typeof obj.message === 'string') {
          return obj.message;
        }
        if ('error' in obj && typeof obj.error === 'string') {
          return obj.error;
        }
        // Fallback: use toString if it's been overridden
        if (
          typeof obj.toString === 'function' &&
          obj.toString !== Object.prototype.toString
        ) {
          return obj.toString();
        }
        return 'Unknown error (cannot stringify)';
      }
    }
  }
  return String(error);
}

/**
 * Categorizes error by checking the error.code property.
 * Returns null if no code-based categorization is possible.
 */
function categorizeByErrorCode(err: any): ErrorCategory | null {
  if (!err.code) {
    return null;
  }

  const code = String(err.code).toUpperCase();

  if (code === 'ETIMEDOUT' || code === 'ESOCKETTIMEDOUT') {
    return ServerErrorCategory.TIMEOUT;
  }
  if (
    code === 'ECONNREFUSED' ||
    code === 'ENOTFOUND' ||
    code === 'ENETUNREACH'
  ) {
    return ServerErrorCategory.NETWORK;
  }
  if (code === 'EAUTH' || code === 'UNAUTHORIZED') {
    return ServerErrorCategory.AUTH;
  }

  return null;
}

/**
 * Categorizes error by checking HTTP status codes.
 * Returns null if no status-based categorization is possible.
 */
function categorizeByStatusCode(err: any): ErrorCategory | null {
  if (!err.status && !err.statusCode) {
    return null;
  }

  const status = err.status ?? err.statusCode;

  if (status === 408 || status === 504) {
    return ServerErrorCategory.TIMEOUT;
  }
  if (status === 401 || status === 403) {
    return ServerErrorCategory.AUTH;
  }
  if (status >= 500 && status < 600) {
    return ServerErrorCategory.SERVER_ERROR;
  }
  if (status === 400) {
    return ServerErrorCategory.BAD_REQUEST;
  }

  return null;
}

/**
 * Categorizes error by searching for keywords in the error message.
 */
function categorizeByMessage(message: string): ErrorCategory {
  const lowerMessage = message.toLowerCase();

  if (lowerMessage.includes('timeout') || lowerMessage.includes('timed out')) {
    return ServerErrorCategory.TIMEOUT;
  }
  if (
    lowerMessage.includes('network') ||
    lowerMessage.includes('connection') ||
    lowerMessage.includes('fetch') ||
    lowerMessage.includes('econnrefused')
  ) {
    return ServerErrorCategory.NETWORK;
  }
  if (
    lowerMessage.includes('auth') ||
    lowerMessage.includes('unauthorized') ||
    lowerMessage.includes('forbidden') ||
    lowerMessage.includes('401') ||
    lowerMessage.includes('403')
  ) {
    return ServerErrorCategory.AUTH;
  }
  if (
    lowerMessage.includes('500') ||
    lowerMessage.includes('502') ||
    lowerMessage.includes('503') ||
    lowerMessage.includes('504')
  ) {
    return ServerErrorCategory.SERVER_ERROR;
  }
  if (lowerMessage.includes('400') || lowerMessage.includes('bad request')) {
    return ServerErrorCategory.BAD_REQUEST;
  }

  return ServerErrorCategory.UNKNOWN;
}

/**
 * Categorizes errors into broad categories for metrics tagging.
 * Uses LOW cardinality categories to avoid metric explosion.
 *
 * Priority order:
 * 1. Check error.code (most reliable, e.g., ETIMEDOUT, ECONNREFUSED)
 * 2. Check error.status/statusCode (HTTP status codes)
 * 3. Fallback to message-based categorization (least reliable)
 */
export function categorizeServerError(error: unknown): ErrorCategory {
  if (!error) {
    return ServerErrorCategory.UNKNOWN;
  }

  // Handle Date objects early to avoid false matches in timezone strings
  if (error instanceof Date) {
    return ServerErrorCategory.UNKNOWN;
  }

  // Priority 1 & 2: Check error codes and HTTP status codes
  if (error != null && typeof error === 'object') {
    // Use != null to check for both null and undefined
    if (!(error instanceof Date) && !(error instanceof RegExp)) {
      const err = error as any;

      const codeCategory = categorizeByErrorCode(err);
      if (codeCategory) {
        return codeCategory;
      }

      const statusCategory = categorizeByStatusCode(err);
      if (statusCategory) {
        return statusCategory;
      }
    }
  }

  // Priority 3: Fallback to message-based classification
  const errorMessage = extractErrorMessage(error);
  return categorizeByMessage(errorMessage);
}
